/* Keep quantity-break images aligned with the selected product options. */
(() => {
  const selector = 'variant-selects[data-quantity-breaks-picker-displayed-images="variant_images"]';
  const observed = new WeakSet();

  function variantImage(variant) {
    return variant?.featured_image?.src || variant?.featured_media?.preview_image?.src || null;
  }

  function imageFor(variants, selected, optionIndex, optionValue, swatchIndexes) {
    const wanted = selected.slice();
    wanted[optionIndex] = optionValue;
    const exact = variants.find(variant => wanted.every((value, index) =>
      value != null && variant.options[index] === value
    ));
    if (variantImage(exact)) return variantImage(exact);

    // A bundle size may have no dedicated image. Keep the fallback in the
    // selected color so a black product never appears under a skin swatch.
    if (!swatchIndexes.length) return null;
    const sameSwatches = variant => swatchIndexes.every(index =>
      wanted[index] != null && variant.options[index] === wanted[index]
    );
    const fallback = variants.find(variant =>
      sameSwatches(variant) && variant.options[optionIndex] === optionValue && variantImage(variant)
    ) || variants.find(variant => sameSwatches(variant) && variantImage(variant));
    return variantImage(fallback);
  }

  function sync(picker) {
    const json = picker.querySelector('script[type="application/json"]');
    if (!json) return;
    let variants;
    try { variants = JSON.parse(json.textContent); } catch { return; }
    if (!Array.isArray(variants)) return;

    const groups = [...picker.children].filter(child =>
      child.classList.contains('product-form__input')
    );
    const selected = groups.map(group =>
      (group.querySelector('input[type="radio"]:checked') || group.querySelector('select'))?.value ?? null
    );
    const swatchIndexes = groups.flatMap((group, index) =>
      group.classList.contains('product-form__input--swatches') ? [index] : []
    );

    groups.forEach((group, index) => {
      if (!group.classList.contains('product-form__input--quantity-breaks')) return;
      const inputs = [...group.querySelectorAll('input[type="radio"]')];
      group.querySelectorAll('label.quantity-break[for]').forEach(label => {
        const input = inputs.find(candidate => candidate.id === label.htmlFor);
        if (!input) return;
        const src = imageFor(variants, selected, index, input.value, swatchIndexes);
        let wrapper = label.querySelector('.quantity-break__image');
        if (!src) {
          if (wrapper) wrapper.hidden = true;
          return;
        }
        if (!wrapper) {
          const container = label.querySelector('.quantity-break__image-and-content');
          if (!container) return;
          wrapper = document.createElement('div');
          wrapper.className = 'quantity-break__image';
          wrapper.append(document.createElement('img'));
          container.prepend(wrapper);
        }
        let img = wrapper.querySelector('img');
        if (!img) {
          img = document.createElement('img');
          wrapper.append(img);
        }
        if (wrapper.hidden) wrapper.hidden = false;
        if (img.getAttribute('src') !== src) img.setAttribute('src', src);
        // A responsive source can otherwise keep displaying the old color.
        if (img.hasAttribute('srcset')) img.removeAttribute('srcset');
        if (img.hasAttribute('sizes')) img.removeAttribute('sizes');
        const alt = selected.map((value, option) => option === index ? input.value : value)
          .filter(Boolean).join(' / ');
        if (img.alt !== alt) img.alt = alt;
      });
    });
  }

  function attach(picker) {
    if (observed.has(picker)) return;
    observed.add(picker);
    let queued = false;
    const schedule = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        if (picker.isConnected) sync(picker);
      });
    };
    picker.addEventListener('change', schedule);
    new MutationObserver(schedule).observe(picker, {
      subtree: true, childList: true, attributes: true,
      attributeFilter: ['src', 'srcset', 'sizes', 'checked']
    });
    schedule();
  }

  function scan(root) {
    if (root.matches?.(selector)) attach(root);
    root.querySelectorAll?.(selector).forEach(attach);
  }

  function start() {
    scan(document);
    new MutationObserver(records => records.forEach(record =>
      record.addedNodes.forEach(node => { if (node.nodeType === 1) scan(node); })
    )).observe(document.documentElement, { subtree: true, childList: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
